import {
  Card as CardContainer,
  CardProps as CardContainerProps,
  useTheme,
} from '@material-ui/core';
import React, { useCallback } from 'react';

export type ThemeColor =
  | 'primary'
  | 'secondary'
  | 'error'
  | 'warning'
  | 'info'
  | 'success'
  | 'default';

export interface CustomCardContainerProps extends CardContainerProps {
  children: React.ReactNode;
  className?: string;
  borderColor?: ThemeColor;
  backgroundColor?: ThemeColor;
  clickable?: boolean;
  loading?: boolean | undefined;
}

const Card: React.FC<CustomCardContainerProps> = ({
  children,
  className = '',
  borderColor,
  backgroundColor,
  clickable,
  loading,
  style,
  ...rest
}: CustomCardContainerProps) => {
  const theme = useTheme();

  const COLOR_BY_THEME: { [key: string]: string } = {
    primary: theme.palette.primary.main,
    secondary: theme.palette.secondary.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
    success: theme.palette.success.main,
    default: theme.palette.grey[400],
  };

  const getCardStyle = useCallback(() => {
    const getThemeBorderColor = (borderColorName: string) => {
      return !loading
        ? COLOR_BY_THEME[borderColorName]
        : COLOR_BY_THEME.default;
    };

    const getThemeColor = (themeColor: string) => {
      return COLOR_BY_THEME[themeColor];
    };

    const styles: React.CSSProperties = {
      ...(clickable ? { cursor: 'pointer' } : {}),
      borderLeft: `4px solid ${getThemeBorderColor(String(borderColor))}`,
      backgroundColor: `${getThemeColor(String(backgroundColor))}`,
      color: `${
        backgroundColor === 'secondary' || backgroundColor === 'primary'
          ? theme.palette.secondary.contrastText
          : 'inherit'
      }`,
      ...style,
    };

    return styles;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    COLOR_BY_THEME,
    backgroundColor,
    borderColor,
    theme.palette.secondary.contrastText,
    style,
    clickable,
  ]);

  return (
    <CardContainer className={className} style={getCardStyle()} {...rest}>
      {children}
    </CardContainer>
  );
};

export default Card;
